// Shared pieces of the Marketing popups and lists (owner, 2026-10-22: "make forms in popup … according
// to the website and compact"): a segmented choice, a compact on/off row, a labelled field, the state
// badge, the summary lines of the popup's side column and a copy-link button.
import { useState, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { Badge, Button, cx, Icon, Popup, useToast, type IconName } from "@ady/ui";
import { PopupHead } from "../finance/MoneyPopups";
import type { RunState } from "./promoApi";

export const frame =
  "min-h-control w-full min-w-0 rounded-xs border border-control bg-surface px-2.5 text-sm outline-none placeholder:text-fg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/** Buttons in a grid, the picked one outlined in brand (like the account type in Money). */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  items,
  cols = 3,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  items: { id: T; label: string; icon?: IconName }[];
  cols?: 2 | 3 | 4;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        "grid gap-1.5",
        cols === 2 ? "grid-cols-2" : cols === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3",
      )}
    >
      {items.map((x) => (
        <button
          key={x.id}
          type="button"
          aria-pressed={value === x.id}
          onClick={() => onChange(x.id)}
          className={cx(
            "flex min-h-control min-w-0 items-center justify-center gap-1.5 rounded-xs border-[1.5px] px-2 text-xs font-medium",
            value === x.id
              ? "ady-outline-brand font-semibold text-link"
              : "border-card bg-surface text-fg-secondary hover:bg-hover",
          )}
        >
          {x.icon ? <Icon name={x.icon} /> : null}
          <span className="truncate">{x.label}</span>
        </button>
      ))}
    </div>
  );
}

/** A compact on/off row: label, an optional hint, and a small switch. */
export function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xs border border-card bg-surface px-2.5 py-1.5">
      <span className="flex min-w-0 flex-col">
        <span className="text-xs font-medium">{label}</span>
        {hint ? <span className="text-2xs text-fg-tertiary">{hint}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cx(
          "relative inline-flex h-5 w-9 flex-none items-center rounded-full border transition-colors",
          checked ? "border-primary bg-primary" : "border-control bg-sunken",
        )}
      >
        <span
          className={cx(
            "absolute size-3.5 rounded-full bg-surface shadow-1 transition-[inset-inline-start]",
            checked ? "start-4.5" : "start-0.5",
          )}
        />
      </button>
    </label>
  );
}

/** A small label over a control, with an optional error or help line. */
export function Field({
  label,
  children,
  error,
  help,
  className,
}: {
  label: string;
  children: ReactNode;
  error?: string;
  help?: string;
  className?: string;
}) {
  return (
    <label className={cx("flex min-w-0 flex-col gap-1", className)}>
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      {children}
      {error ? (
        <span className="text-2xs font-medium text-danger-fg">{error}</span>
      ) : help ? (
        <span className="text-2xs text-fg-tertiary">{help}</span>
      ) : null}
    </label>
  );
}

const STATE_TONE: Record<RunState, "success" | "action" | "neutral" | "warning"> = {
  running: "success",
  scheduled: "action",
  paused: "warning",
  ended: "neutral",
};

export function StateBadge({ state }: { state: RunState }) {
  return <Badge tone={STATE_TONE[state]}>{t(`marketing.promo.state.${state}`)}</Badge>;
}

/** Label/value lines in the popup's sunken side column. */
export function SummaryLines({ rows, title }: { title: string; rows: [string, ReactNode][] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">{title}</span>
      {rows.map(([k, v]) => (
        <span key={k} className="flex items-start justify-between gap-2 text-xs">
          <span className="text-fg-secondary">{k}</span>
          <span className="min-w-0 text-end font-semibold break-words" data-content="user">
            {v}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Copies a text (a link) and says so. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const toast = useToast();
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => toast.show({ tone: "success", title: t("marketing.promo.copied") }))
          .catch(() => toast.show({ tone: "error", title: t("marketing.promo.copyFailed") }));
      }}
      className="inline-flex size-7 flex-none items-center justify-center rounded-xs text-fg-secondary hover:bg-hover hover:text-link"
      title={label}
    >
      <Icon name="copy" />
      <span className="ady-sr-only">{label}</span>
    </button>
  );
}

/** "2026-10-22T14:30" for a datetime-local input, in the browser's zone. */
export const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const fromLocalInput = (v: string): string | null => (v ? new Date(v).toISOString() : null);

/** The remaining time as "2d 4h" / "3h 12m" / "8m". */
export function timeLeft(iso: string, now = Date.now()): string {
  const ms = Math.max(0, Date.parse(iso) - now);
  const m = Math.floor(ms / 60_000);
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d > 0) return t("marketing.promo.left.dh", { d, h });
  if (h > 0) return t("marketing.promo.left.hm", { h, m: m % 60 });
  return t("marketing.promo.left.m", { m: Math.max(1, m) });
}

/** A small "Delete …?" popup: `ask({...})` opens it; render `element` once in the page. */
export function useConfirm() {
  const [ask, setAsk] = useState<{
    title: string;
    body: string;
    action: string;
    onConfirm: () => void;
    /** A brand (not danger) action, e.g. Send. */
    primary?: boolean;
    icon?: IconName;
  } | null>(null);
  const close = () => setAsk(null);
  const element = (
    <Popup open={ask !== null} onClose={close} size="sm" label={ask?.title ?? ""}>
      {ask ? (
        <div className="flex flex-col">
          <PopupHead icon={ask.icon ?? "trash"} title={ask.title} onClose={close} />
          <p className="pt-3 text-start text-sm text-fg-secondary">{ask.body}</p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={close}>
              {t("payments.money.cancel")}
            </Button>
            <Button
              variant={ask.primary ? "primary" : "danger"}
              icon={ask.icon ?? "trash"}
              onClick={() => {
                ask.onConfirm();
                close();
              }}
            >
              {ask.action}
            </Button>
          </div>
        </div>
      ) : null}
    </Popup>
  );
  return { ask: setAsk, element };
}
